/**
 * 3乗と 0.8 乗を、実行環境の数学ライブラリに頼らず「正しく丸めた」値で返す (2026-10-13)。
 *
 * 理由: `x ** 3` と `x ** 0.8` の最後の1ビットが、Node/V8 の作りによって違う。
 *   Linux 版 Node 22 では約1割の入力で最後の1ビットがずれ、Windows の値 (正しく丸めた値) と合わなかった
 *   (tests/awoken_wall_hunt_game.test.ts「b51982a0 round-trip tick traces」が Linux だけ赤・原因の調べ＝docs/research/2026-10-13-wallhunt-linux-trace-mismatch.md)。
 *   ゲームの動きは1ビットで変わらないが、往復の記録は一つ一つの数を sha256 で固めているので、この関数で環境差を無くす。
 *   Windows の Node で出ていた値 (= 正しく丸めた値) を返すので、Windows の記録は変わらない。
 * 正しく丸める = 整数 (BigInt) で十分な桁数まで計り、最後に1回だけ最も近い偶数へ丸める。 非有限・0・非正規数は元の `**` に任せる。
 */
const F64 = new DataView(new ArrayBuffer(8))

/** 有限・正規の正の double を m * 2^e (m は 53 ビットの整数) に分ける。 できなければ undefined */
function split(x: number): { m: bigint; e: number } | undefined {
  F64.setFloat64(0, x)
  const hi = F64.getUint32(0), biased = (hi >>> 20) & 0x7ff
  if (biased === 0 || biased === 0x7ff) return undefined
  return { m: (BigInt(hi & 0xfffff | 0x100000) << 32n) | BigInt(F64.getUint32(4)), e: biased - 1075 }
}

function bitLength(n: bigint): number {
  let b = Math.floor(Math.log2(Number(n))) + 1   // n ≥ 1。 誤差の分だけ補正する
  while ((n >> BigInt(b)) > 0n) b++
  while (b > 1 && (n >> BigInt(b - 1)) === 0n) b--
  return b
}

/** n * 2^exp2 (sticky = 本当の値は n より少し大きい) を最も近い偶数へ丸めた double。 範囲外は undefined */
function roundToDouble(n: bigint, exp2: number, sticky: boolean): number | undefined {
  const shift = bitLength(n) - 53
  if (shift < 1) return undefined
  let q = n >> BigInt(shift)
  const rem = n & ((1n << BigInt(shift)) - 1n), half = 1n << BigInt(shift - 1)
  if (rem > half || (rem === half && (sticky || (q & 1n) === 1n))) q += 1n
  const scale = exp2 + shift
  if (scale < -1000 || scale > 1000) return undefined
  F64.setUint32(0, (scale + 1023) << 20); F64.setUint32(4, 0)   // 2^scale (正規数の範囲)
  return Number(q) * F64.getFloat64(0)
}

/**
 * x ** 3 (正しく丸めた値)。 負の x は符号を付けて返す。
 *   ふつうの大きさの数は、2倍精度の2つ組 (Dekker・誤差を落とさない掛け算) で t^3 = q + (小さな残り) を作り、1回だけ丸める (BigInt より速い)。
 *   極端に大きい・小さい数だけ BigInt の厳密計算に回す。
 */
export function exactCube(x: number): number {
  const a = Math.abs(x)
  if (a > 1e-60 && a < 1e60) {
    const SPLIT = 134217729   // 2^27 + 1 (Veltkamp)
    let c = SPLIT * a; const ah = c - (c - a), al = a - ah
    const p = a * a, pe = ((ah * ah - p) + 2 * ah * al) + al * al   // a² = p + pe (厳密)
    c = SPLIT * p; const ph = c - (c - p), pl = p - ph
    const q = p * a, qe = ((ph * ah - q) + ph * al + pl * ah) + pl * al   // p·a = q + qe (厳密)
    const r = q + (qe + pe * a)
    return x < 0 ? -r : r
  }
  const neg = x < 0, parts = split(neg ? -x : x)
  const r = parts && roundToDouble(parts.m ** 3n, 3 * parts.e, false)
  return r === undefined ? x ** 3 : neg ? -r : r
}

/** 有限の double v を v * 2^k の整数 (小数点以下は切り捨て) にする。 */
function toFixed(v: number, k: number): bigint {
  if (v === 0) return 0n
  const parts = split(Math.abs(v))
  if (!parts) return 0n
  const sh = parts.e + k, n = sh >= 0 ? parts.m << BigInt(sh) : parts.m >> BigInt(-sh)
  return v < 0 ? -n : n
}

const POW08_CACHE = new Map<number, number>()

/**
 * x ** 0.8 (正しく丸めた値・x ≥ 0)。 0.8 は double の 0.8 (= 4/5 + 1/(5·2^52)) として計る。
 *   x^(4/5) = x^4 の5乗根を 100 ビット超の整数で (ニュートン法・出だしは Math.pow の近似) 取り、
 *   残りの x^(1/(5·2^52)) = 1 + ln(x)/(5·2^52) を掛けてから、1回だけ丸める。 同じ x は覚えておく (往復の記録は同じ値を何度も使う)。
 */
export function exactPow08(x: number): number {
  const known = POW08_CACHE.get(x)
  if (known !== undefined) return known
  const parts = x > 0 ? split(x) : undefined
  if (!parts) return x ** 0.8
  const s = Math.ceil(64 - 0.8 * parts.e), target = (parts.m ** 4n) << BigInt(4 * parts.e + 5 * s)   // 5乗根を取りたい整数
  let r = toFixed(x ** 0.8, s)   // 近似 (52 ビット) → 2回で 100 ビット超
  for (let i = 0; i < 3; i++) r = (4n * r + target / (r ** 4n)) / 5n
  const K = 130, eps = toFixed(Math.log(x) / (5 * 4503599627370496), K)
  const value = roundToDouble(r + ((r * eps) >> BigInt(K)), -s, true) ?? x ** 0.8
  if (POW08_CACHE.size > 4096) POW08_CACHE.clear()
  POW08_CACHE.set(x, value)
  return value
}
